import type { ReactNode } from "react";
import FullscreenIcon from "@/assets/icons/fullscreen.svg?react";
import { CloseOutlined } from "@ant-design/icons";

function toSafeTestId(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type Header = {
  key: string;
  value: string | ReactNode;
};

type DrawerHeaderProps = {
  title: ReactNode;
  headersArray?: Header[];
  onChangeSize?: () => void;
  onClose?: () => void;
  actions?: ReactNode;
};

export default function DrawerHeader({
  title,
  headersArray,
  onChangeSize,
  onClose,
  actions,
}: DrawerHeaderProps) {
  const hasHeaders = (headersArray?.length ?? 0) > 0;

  return (
    <div className="flex flex-col text-white">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1" data-testid="drawer-header-title">
          {title}
        </div>
        <div className="flex shrink-0 gap-3">
          {actions ?? null}
          {onChangeSize ? (
            <button
              type="button"
              aria-label="Toggle drawer size"
              data-testid="drawer-size-toggle-button"
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 leading-none text-white"
              onClick={onChangeSize}
            >
              <FullscreenIcon className="h-4 w-4 shrink-0" />
            </button>
          ) : null}
          {onClose ? (
            <button
              type="button"
              aria-label="Close drawer"
              data-testid="drawer-close-button"
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 leading-none text-white"
              onClick={onClose}
            >
              <CloseOutlined className="text-base text-white" />
            </button>
          ) : null}
        </div>
      </div>

      {hasHeaders ? (
        <div className="flex flex-col mt-2">
          {headersArray!.map((header, index) => (
            <div
              className="flex items-center justify-between mb-1"
              data-testid={`drawer-header-row-${toSafeTestId(header.key)}`}
              key={index}
            >
              <div className="text-sm text-[#C4CDD0]">{header.key}</div>
              <div
                className="text-sm text-[#d3d3d3]"
                data-testid={`drawer-header-value-${toSafeTestId(header.key)}`}
              >
                {header.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
